import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import EditProfileModal from '../ui/EditProfileModal';

const Profile: React.FC = () => {
    const { currentUser, userCredits, userProfile } = useAuth();
    const navigate = useNavigate();

    // Basic user info
    const userEmail = currentUser?.email || '';
    const [avatarError, setAvatarError] = useState(false);
    const photoURLRaw = currentUser?.photoURL || '';
    const photoURL = photoURLRaw.trim();
    const hasPhoto = photoURL.length > 0;
    const joinDate = currentUser?.metadata?.creationTime
        ? new Date(currentUser.metadata.creationTime).toLocaleDateString()
        : 'Unknown';

    // UI states
    const [success, setSuccess] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);

    // Helper function for showing success messages
    const showSuccessMessage = (message: string) => {
        setSuccess(message);
        setTimeout(() => {
            setSuccess('');
        }, 3000);
    };

    // Handle successful profile edit
    const handleEditSuccess = () => {
        showSuccessMessage('Profile updated successfully!');
    };

    // Show loading if profile data is not yet loaded
    if (!userProfile && currentUser) {
        return (
            <div className="p-6 max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold mb-6">Profile</h1>
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
                    <span className="ml-2 text-text-description">Loading profile...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Profile</h1>

            {/* Success Message */}
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                    <i className="fas fa-check-circle mr-2"></i>
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Card */}
                <div className="lg:col-span-1">
                    <div className="bg-white p-6 rounded-lg shadow h-full">
                        <div className="text-center">
                            {hasPhoto && !avatarError ? (
                                <img
                                    src={photoURL}
                                    alt="Profile"
                                    onError={() => setAvatarError(true)}
                                    className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-accent-taupe object-cover"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-accent-taupe bg-accent-taupe flex items-center justify-center overflow-hidden">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        className="w-12 h-12 text-text-description"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path fillRule="evenodd" d="M18 8a6 6 0 11-12 0 6 6 0 0112 0zm-6 8c-5.523 0-10 2.239-10 5v1h20v-1c0-2.761-4.477-5-10-5z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            )}

                            <h2 className="text-xl font-semibold text-text-heading mb-2">
                                {userProfile?.name || currentUser?.displayName || userEmail.split('@')[0] || 'User'}
                            </h2>

                            <p className="text-text-description text-sm">{userEmail}</p>

                            <div className="mt-4 pt-4 border-t border-accent-taupe">
                                <div className="flex items-center justify-center space-x-2 bg-yellow-100 rounded-full py-2 px-4">
                                    <i className="fas fa-coins text-yellow-600"></i>
                                    <span className="text-yellow-600 font-medium">
                                        {(userProfile?.credits !== undefined ? userProfile.credits : userCredits).toFixed(2)} Credits
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Account Information */}
                <div className="lg:col-span-2">
                    <div className="bg-white p-6 rounded-lg shadow h-full">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Account Information</h3>
                            <button
                                onClick={() => setShowEditModal(true)}
                                className="inline-flex items-center px-4 py-2 bg-secondary-500 text-white rounded-lg text-sm hover:bg-secondary-600 transition-colors"
                            >
                                <i className="fas fa-edit mr-2 text-xs"></i>
                                Edit Profile
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-text-heading mb-1">Phone Number</label>
                                <div className="bg-accent-sand p-3 rounded-md">
                                    <p className="text-sm text-text-heading font-mono break-all">
                                        {userProfile?.phoneNumber || 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-heading mb-1">Email Address</label>
                                <div className="bg-accent-sand p-3 rounded-md">
                                    <p className="text-sm text-text-heading">
                                        {userProfile?.emailId || userEmail}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-heading mb-1">Member Since</label>
                                <div className="bg-accent-sand p-3 rounded-md">
                                    <p className="text-sm text-text-heading">
                                        {userProfile?.registrationDate
                                            ? new Date(userProfile.registrationDate).toLocaleDateString()
                                            : joinDate
                                        }
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-heading mb-1">Company Name</label>
                                <div className="bg-accent-sand p-3 rounded-md">
                                    <p className="text-sm text-text-heading">
                                        {userProfile?.companyName || 'Not specified'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => navigate('/account-settings')}
                        className="bg-secondary-500 text-white py-3 px-4 rounded-md hover:bg-secondary-600 transition-colors"
                    >
                        <i className="fas fa-cog mr-2"></i>
                        Account Settings
                    </button>

                    <button
                        onClick={() => navigate('/subscription')}
                        className="bg-yellow-600 text-white py-3 px-4 rounded-md hover:bg-yellow-700 transition-colors"
                    >
                        <i className="fas fa-crown mr-2"></i>
                        Upgrade Plan
                    </button>

                    <button
                        onClick={() => navigate('/credit-history')}
                        className="bg-gray-600 text-white py-3 px-4 rounded-md hover:bg-gray-700 transition-colors"
                    >
                        <i className="fas fa-history mr-2"></i>
                        View History
                    </button>
                </div>
            </div>

            {/* Edit Profile Modal */}
            <EditProfileModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSuccess={handleEditSuccess}
            />
        </div>
    );
};

export default Profile;